// Copyright (c) Docugami, Inc. All rights reserved.

import { ScrollablePane, SelectionMode, Text } from "@fluentui/react";
import { getIconForFileViewItem } from "components/file_management/presentation/DocumentViewer";
import { AddDocsListView, ListItem } from "components/myContentExplorer/AddDocsListView";
import { primaryTheme } from "components/shell/presentation/AppStyles";
import { compare, ProviderId, ProviderItem } from "networking/StorageProviders";
import { useMemo, useState } from "react";
import { getProviderFriendlyName } from "storage/StorageProviderHelpers";
import styled from "styled-components";
import { ProviderPickerItem } from "./ProviderPickerItem";
import { StorageProviderPickerCommands, StorageProviderPickerModal } from "./StorageProviderItemPickerModal";

const RootContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 500px;
    gap: 5px;
    padding: 5px;
    box-sizing: border-box;
`

const ContentContainer = styled.div`
    display: flex;
    flex: 3;
    flex-direction: column;
    gap: 5px;
    padding: 20px;
    max-width: 1000px;
`

const ItemsContainer = styled.div`
    flex: 1;
    width: 85%;
    position: relative;
    align-content: center;
    justify-content: center;
    place-self: center;
    border: 1px solid ${primaryTheme.palette.neutralDark};
`;

interface SyncModalProps {
    providerId: ProviderId | null;
    items: ProviderPickerItem[];
    onConfirm: (items: ProviderPickerItem[]) => void;
}

export const SyncModal = (props: SyncModalProps): JSX.Element => {
    const [selectedIds, setSelectedIds] = useState<ProviderItem[]>([]);
    const { items, providerId, onConfirm } = props;

    function onSelectionChanged(items: ProviderListItem[]) {
        setSelectedIds(items.map(x => x.id.item));
    }

    function onComplete() {
        const itemsToConfirm = items.map(item => {
            if (selectedIds.find(id => compare(item.item, id, item.providerId))) {
                // Item is selected
                return {
                    ...item,
                    isSynced: true
                }
            }

            return item;
        })

        onConfirm(itemsToConfirm);
    }

    const subText = `Enabling auto-sync will allow us to connect to your ${providerId ? getProviderFriendlyName(providerId) : "remote"} folder. Any files added to this folder will be synced to Docugami and processed automatically. Once saved, the auto-sync setting cannot be changed in the future.`

    const listItems: ProviderListItem[] = useMemo(() => items.map(pickerItemToListItem), [items])

    return (
        <StorageProviderPickerModal isOpen={!!items.length}>
            <RootContainer>
                <ContentContainer>
                    <Text variant={'xLargePlus'}>Choose folders to sync to Docugami</Text>
                    <Text variant={'mediumPlus'}>{subText}</Text>
                    <br />
                    <ItemsContainer>
                        <ScrollablePane>
                            <AddDocsListView
                                selectionMode={SelectionMode.multiple}
                                items={listItems}
                                onSelectionChanged={onSelectionChanged} />
                        </ScrollablePane>
                    </ItemsContainer>
                </ContentContainer>
                <StorageProviderPickerCommands
                    primaryAction={{
                        label: 'Finish',
                        onClick: onComplete
                    }} />
            </RootContainer>
        </StorageProviderPickerModal>
    )
}

interface ProviderListItem extends ListItem {
    id: ProviderPickerItem;
}

const pickerItemToListItem = (item: ProviderPickerItem): ProviderListItem => {
    return {
        id: item,
        name: item.name,
        icon: getIconForFileViewItem(item.isFolder, item.providerId),
        autoSync: item.isSynced
    }
}